class FeatureTextGroup extends HTMLElement {
  #tooltip = null;
  #tooltipInner = null;
  #mobileBar = null;
  #mobileBarWrap = null;
  #mobileQuery = window.matchMedia('(max-width: 989px)');

  connectedCallback() {
    this.#assignPositions();
    this.#buildTooltip();
    this.#buildMobileBar();
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#tooltip?.remove();
  }

  #assignPositions() {
    this.querySelectorAll('.kt-feature-text').forEach((item, index) => {
      for (let i = 1; i <= 5; i++) item.classList.remove(`kt-feature-text--position-${i}`);
      if (index < 5) item.classList.add(`kt-feature-text--position-${index + 1}`);

      const color = item.dataset.color || 'rgb(255, 255, 255)';
      item.style.setProperty(
        '--feature-gradient',
        `radial-gradient(50% 50% at 50% 50%, ${color} 55.77%, rgba(4, 4, 4, 0.6) 100%)`,
      );
    });
  }

  #buildTooltip() {
    this.#tooltipInner = document.createElement('div');
    this.#tooltipInner.className = 'kt-feature-text-tooltip__inner';

    this.#tooltip = document.createElement('div');
    this.#tooltip.className = 'kt-feature-text-tooltip';
    this.#tooltip.setAttribute('aria-hidden', 'true');
    this.#tooltip.appendChild(this.#tooltipInner);

    this.appendChild(this.#tooltip);
  }

  #buildMobileBar() {
    const items = [...this.querySelectorAll('.kt-feature-text')];
    if (!items.length) return;

    const bar = document.createElement('div');
    bar.className = 'kt-feature-text-mobile-bar';

    items.forEach((item, index) => {
      const label = item.querySelector('.kt-feature-text__label');
      if (!label) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'kt-feature-text-mobile-bar__item';
      button.textContent = label.textContent.trim();
      button.dataset.featureIndex = String(index);

      const color = item.dataset.color;
      if (color) button.style.setProperty('--feature-accent', color);

      button.addEventListener('click', () => this.#onMobileItemClick(item, button));
      bar.appendChild(button);
    });

    if (!bar.children.length) return;

    const wrap = document.createElement('div');
    wrap.className = 'kt-feature-text-mobile-bar-wrap';

    const chevronLeft = document.createElement('span');
    chevronLeft.className = 'kt-feature-text-mobile-bar__chevron kt-feature-text-mobile-bar__chevron--left';
    chevronLeft.setAttribute('aria-hidden', 'true');

    const chevronRight = document.createElement('span');
    chevronRight.className = 'kt-feature-text-mobile-bar__chevron kt-feature-text-mobile-bar__chevron--right';
    chevronRight.setAttribute('aria-hidden', 'true');

    wrap.append(chevronLeft, bar, chevronRight);

    this.#titleEl?.after(wrap);
    this.#mobileBar = bar;
    this.#mobileBarWrap = wrap;

    bar.addEventListener('scroll', () => this.#updateMobileBarFade(), { passive: true });
    this.#mobileQuery.addEventListener('change', () => this.#updateMobileBarFade());
    requestAnimationFrame(() => this.#updateMobileBarFade());
  }

  #updateMobileBarFade() {
    const bar = this.#mobileBar;
    const wrap = this.#mobileBarWrap;
    if (!bar || !wrap) return;

    const scrollable = bar.scrollWidth > bar.clientWidth + 1;
    const atStart = bar.scrollLeft <= 1;
    const atEnd = bar.scrollLeft + bar.clientWidth >= bar.scrollWidth - 1;

    wrap.classList.toggle('is-fade-end', scrollable && !atEnd);
    wrap.classList.toggle('is-fade-start', scrollable && !atStart);
  }

  #isMobile() {
    return this.#mobileQuery.matches;
  }

  #mobileGradient(gradient) {
    return gradient
      .replace(/radial-gradient\(\s*50% 50%/, 'radial-gradient(100% 100%')
      .replace(/55\.77%/, '85%')
      .replace('rgba(4, 4, 4, 0.6)', 'rgba(4, 4, 4, 0.9)');
  }

  #getSectionCenter() {
    const sectionEl = this.closest('.shopify-section') || this.parentElement || this;
    const rect = sectionEl.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  #getSide(item) {
    const groupRect = this.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const itemCenterX = itemRect.left + itemRect.width / 2;
    const itemCenterY = itemRect.top + itemRect.height / 2;
    const groupCenterX = groupRect.left + groupRect.width / 2;
    const groupCenterY = groupRect.top + groupRect.height / 2;
    return {
      horizontal: itemCenterX < groupCenterX ? 'left' : 'right',
      // pill above section center → fly in from top (negative Y); below → from bottom (positive Y)
      flyY: itemCenterY < groupCenterY ? -24 : 24,
    };
  }

  #bindEvents() {
    this.querySelectorAll('.kt-feature-text').forEach(item => {
      item.addEventListener('mouseenter', () => {
        if (!this.#isMobile()) this.#show(item);
      });
      item.addEventListener('mouseleave', () => {
        if (!this.#isMobile()) this.#hide();
      });
    });

    this.#tooltip.addEventListener('click', () => {
      if (this.#isMobile()) this.#hide();
    });
  }

  #onMobileItemClick(item, button) {
    if (item.classList.contains('is-active') && this.#tooltip.classList.contains('is-visible')) {
      this.#hide();
      return;
    }

    this.#show(item);
    this.#mobileBar?.querySelectorAll('.kt-feature-text-mobile-bar__item').forEach(btn => {
      btn.classList.toggle('is-active', btn === button);
    });
  }

  #show(item) {
    const textEl = item.querySelector('.kt-feature-text__text');
    if (!textEl) return;

    const isMobile = this.#isMobile();

    this.querySelectorAll('.kt-feature-text.is-active').forEach(el => el.classList.remove('is-active'));
    item.classList.add('is-active');

    this.#tooltipInner.innerHTML = textEl.innerHTML;
    let itemGradient = getComputedStyle(item).getPropertyValue('--feature-gradient').trim();
    if (!itemGradient) {
      itemGradient = 'radial-gradient(50% 50% at 50% 50%, rgb(255, 255, 255) 55.77%, rgba(4, 4, 4, 0.6) 100%)';
    }
    if (isMobile) {
      itemGradient = this.#mobileGradient(itemGradient);
    }
    this.#tooltipInner.style.setProperty('--feature-gradient', itemGradient);
    this.#tooltipInner.style.textTransform = item.dataset.textTransform || '';

    this.#tooltip.classList.remove('is-visible', 'from-left', 'from-right', 'is-mobile-layout');

    if (isMobile) {
      this.#tooltip.style.left = '';
      this.#tooltip.style.top = '';
      this.#tooltip.style.height = '';
      this.#tooltipInner.style.removeProperty('--fly-from-x');
      this.#tooltipInner.style.removeProperty('--fly-from-y');
      void this.#tooltip.offsetWidth;
      this.#tooltip.classList.add('is-visible', 'is-mobile-layout');
    } else {
      const groupRect = this.getBoundingClientRect();
      const { horizontal: side, flyY } = this.#getSide(item);
      const flyX = side === 'left' ? -40 : 40;

      this.#tooltip.style.left = `${groupRect.width / 2}px`;
      this.#tooltip.style.top = `${groupRect.height / 2}px`;
      this.#tooltip.style.height = `${groupRect.height}px`;
      this.#tooltipInner.style.setProperty('--fly-from-x', `${flyX}px`);
      this.#tooltipInner.style.setProperty('--fly-from-y', `${flyY}px`);
      void this.#tooltip.offsetWidth;
      this.#tooltip.classList.add('is-visible', `from-${side}`);
      this.#animateTitle(side === 'left' ? 30 : -30, -flyY);
      this.#animateOtherLabels(item, side === 'left' ? 14 : -14, -flyY * 0.5);
    }

    this.#tooltip.setAttribute('aria-hidden', 'false');
  }

  #hide() {
    this.#tooltip.classList.remove('is-visible', 'from-left', 'from-right', 'is-mobile-layout');
    this.#tooltip.setAttribute('aria-hidden', 'true');
    this.querySelectorAll('.kt-feature-text.is-active').forEach(el => el.classList.remove('is-active'));
    this.#mobileBar?.querySelectorAll('.kt-feature-text-mobile-bar__item.is-active').forEach(btn => {
      btn.classList.remove('is-active');
    });

    if (!this.#isMobile()) {
      this.#animateTitle(0, 0);
      this.#animateOtherLabels(null, 0, 0);
    }
  }

  // Returns the section header element (first direct child that isn't a pill)
  get #titleEl() {
    return Array.from(this.children).find(child => !child.classList.contains('kt-feature-text'));
  }

  #animateTitle(targetX, targetY) {
    const el = this.#titleEl;
    if (!el || typeof el.animate !== 'function') return;

    const currentTransform = el.style.transform || 'translate(0px, 0px)';
    const matchX = currentTransform.match(/translateX\((-?[\d.]+)px\)/) || currentTransform.match(/translate\((-?[\d.]+)px/);
    const matchY = currentTransform.match(/translateY\((-?[\d.]+)px\)/) || currentTransform.match(/translate\([^,]+,\s*(-?[\d.]+)px/);
    const currentX = matchX ? parseFloat(matchX[1]) : 0;
    const currentY = matchY ? parseFloat(matchY[1]) : 0;

    el.getAnimations().forEach(a => a.cancel());

    const animation = el.animate(
      [
        { transform: `translate(${currentX}px, ${currentY}px)`, offset: 0 },
        { transform: `translate(${targetX}px, ${targetY}px)`, offset: 1 },
      ],
      { duration: 400, easing: 'ease-out', fill: 'forwards' },
    );

    animation.onfinish = () => {
      el.style.transform = `translate(${targetX}px, ${targetY}px)`;
    };
  }

  #animateOtherLabels(activeItem, targetX, targetY) {
    this.querySelectorAll('.kt-feature-text').forEach(item => {
      if (item === activeItem) return;

      const currentTransform = item.style.transform || 'translate(0px, 0px)';
      const matchX = currentTransform.match(/translateX\((-?[\d.]+)px\)/) || currentTransform.match(/translate\((-?[\d.]+)px/);
      const matchY = currentTransform.match(/translateY\((-?[\d.]+)px\)/) || currentTransform.match(/translate\([^,]+,\s*(-?[\d.]+)px/);
      const currentX = matchX ? parseFloat(matchX[1]) : 0;
      const currentY = matchY ? parseFloat(matchY[1]) : 0;

      item.getAnimations().forEach(a => a.cancel());

      const animation = item.animate(
        [
          { transform: `translate(${currentX}px, ${currentY}px)`, offset: 0 },
          { transform: `translate(${targetX}px, ${targetY}px)`, offset: 1 },
        ],
        { duration: 400, easing: 'ease-out', fill: 'forwards' },
      );

      animation.onfinish = () => {
        item.style.transform = `translate(${targetX}px, ${targetY}px)`;
      };
    });
  }
}

if (!customElements.get('feature-text-group')) {
  customElements.define('feature-text-group', FeatureTextGroup);
}
